import {
	AppButton,
	AppIconButton,
	Badge,
	Checkbox,
	Dropdown,
	Input,
	Textarea,
	Tooltip,
	useEnterExit
} from '@clickguard/design-system'
import LayersTwo01 from '@untitled-ui/icons-react/build/cjs/LayersTwo01'
import { WebsiteAvatar } from 'components'
import { ChangePlanDialog } from 'pages/workspace/billing/subscription-tab/actions/change-plan-dialog'
import { useAccountStore } from 'store'
import { PostHogPath } from 'utils/posthog-events'
import { XClose } from 'components/icons'
import {
	FormEvent,
	KeyboardEvent as ReactKeyboardEvent,
	PointerEvent as ReactPointerEvent,
	ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import { createPortal } from 'react-dom'
import {
	CLICK_STATUS_OPTIONS,
	DEFAULT_RULE_DRAFT,
	FOCUS_OPTIONS,
	PERIOD_OPTIONS,
	SCOPE_OPTIONS
} from './rule-options'
import {
	CRITERIA_DEFINITIONS,
	CriterionDefinition,
	criterionDefinition,
	isCriterionGated
} from './criteria'
import { countryCodeByName } from 'components/country-select/countries'
import { CountryPicker } from './country-picker'
import { FadingScrollArea } from 'components/fading-scroll/fading-scroll-area'
import { KeywordInput } from './keyword-input'
import { MultiSelect } from './multi-select'
import { resolveRulePlatform } from './rule-adapters'
import { RuleEntities, useRuleEntities } from './rule-entities'
import { RuleLedger } from './rule-ledger'
import {
	ClickStatus,
	CriterionOperator,
	CustomRule,
	RuleCriterion,
	RuleDraft,
	RuleScopeMode,
	RuleTemplate
} from './types'

interface RuleSheetProps {
	open: boolean
	rule?: CustomRule | null
	/** Prefill for a brand-new rule; ignored when editing an existing `rule`. */
	initialDraft?: RuleDraft | null
	/** Name of the template a create-flow draft came from — shows a header chip. */
	templateName?: string
	/** Set when the sheet edits a template rather than a rule. */
	template?: RuleTemplate | null
	onClose: () => void
	onSave: (rule: CustomRule) => void | Promise<void>
	onSaveTemplate?: (draft: RuleDraft) => void | Promise<void>
}

// Deep-ish clone so local edits never mutate the source rule or template.
const cloneDraft = (draft: RuleDraft): RuleDraft => ({
	name: draft.name,
	description: draft.description,
	scope: { ...draft.scope, values: [...draft.scope.values] },
	trigger: { ...draft.trigger },
	criteria: draft.criteria.map((criterion) => ({
		...criterion,
		values: [...criterion.values]
	})),
	threshold: { ...draft.threshold },
	actions: { ...draft.actions }
})

/** Slide duration. Must stay in step with the `duration-300` classes below. */
const SHEET_MOTION_MS = 300

/** Cap on the Summary band's height; the drag moves the cap, not the height. */
const SUMMARY_CAP_DEFAULT = 230
const SUMMARY_CAP_MIN = 62
const SUMMARY_CAP_MAX = 420
const SUMMARY_FORM_FLOOR = 220
/** Keyboard step, and the coarse one on Shift. */
const SUMMARY_CAP_STEP = 16
const SUMMARY_CAP_STEP_COARSE = 48

const draftFromRule = (
	rule?: CustomRule | null,
	initialDraft?: RuleDraft | null
): RuleDraft => {
	if (rule) return cloneDraft(rule)
	if (initialDraft) return cloneDraft(initialDraft)
	return DEFAULT_RULE_DRAFT
}

// 14px medium, matching the DS Input's own `label`. NB this app's Tailwind scale
// is shifted (text-base = 14px, text-sm = 12px).
const FieldLabel = ({
	children,
	muted
}: {
	children: string
	muted?: boolean
}) => (
	<span
		className={`mb-1.5 block text-base font-medium ${
			muted
				? 'text-[rgb(var(--cg-text-disabled))]'
				: 'text-[rgb(var(--cg-text-primary))]'
		}`}
	>
		{children}
	</span>
)

const SectionTitle = ({ title, hint }: { title: string; hint: string }) => (
	<div className="mb-4">
		<h3 className="text-lg font-semibold text-[rgb(var(--cg-text-primary))]">
			{title}
		</h3>
		<p className="mt-0.5 text-[13px] leading-[18px] text-[rgb(var(--cg-text-secondary))]">
			{hint}
		</p>
	</div>
)

const Section = ({ children }: { children: ReactNode }) => (
	<div className="py-5 first:pt-1 last:pb-1">{children}</div>
)

// The DS Dropdown wraps its trigger in a `w-fit` span; these selectors reach through.
const stretchTrigger =
	'[&>button]:w-full [&>button]:justify-between [&>div>button]:w-full [&>div>button]:justify-between'

const fullWidthTrigger = `w-full ${stretchTrigger}`

// The favicon must live INSIDE `label`: the trigger renders only the selected
// option's label and drops `leadingIcon`. The chip rides in `shortcut`, the only
// right-hand slot Dropdown forwards.
const scopeChoices = (website: RuleEntities['website']) =>
	SCOPE_OPTIONS.map((option) =>
		option.value === 'website'
			? {
					...option,
					label: (
						<span className="flex min-w-0 items-center gap-2">
							<WebsiteAvatar website={website} size={4} />
							<span className="min-w-0 truncate">{website.name}</span>
						</span>
					),
					shortcut: (
						<Badge variant="gray" size="sm">
							All ad traffic
						</Badge>
					)
			  }
			: option
	)

const THRESHOLD_MODE_OPTIONS = [
	{ value: 'all', label: 'All clicks' },
	{ value: 'percentage', label: 'A percentage of clicks' }
]

// The fields borrow the Dropdown trigger's surface; ! because twMerge cannot
// dedupe the DS `cg-` classes.
const FIELD_SHADOW =
	'shadow-[0_1px_2px_0_rgba(16,24,40,0.05),inset_0_-2px_1px_0_rgba(0,0,0,0.03)]'
const fieldSurface = `[&:not(:focus-within)]:!border-[rgb(var(--cg-border))] ${FIELD_SHADOW}`
// Spelled out because the resting border above is pinned with `!important`.
const fieldSurfaceInvalid = `[&:not(:focus-within)]:!border-[rgb(var(--cg-danger))] ${FIELD_SHADOW}`
const surfaceFor = (invalid: boolean) =>
	invalid ? fieldSurfaceInvalid : fieldSurface

const invalidControl = '[&_button]:!border-[rgb(var(--cg-danger))]'

/** Matches the message the DS Input renders for its own `error` prop. */
const FieldError = ({ children }: { children?: string }) =>
	children ? (
		<p
			role="alert"
			className="mt-1.5 text-base leading-[18px] text-[rgb(var(--cg-danger))]"
		>
			{children}
		</p>
	) : null

// The DS Tooltip's `inline-flex` wrapper shrinks the row to its text — reach through.
const TOOLTIP_FIT = '[&>div]:w-full'

// `block w-fit`: the DS body is `w-max max-w-xs`, so an inline link measures as
// one long line and wraps against the cap.
const UPGRADE_LINK =
	'mt-1 block w-fit rounded-sm font-medium text-white underline underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'

interface CriterionRowProps {
	definition: CriterionDefinition
	selected?: RuleCriterion
	/** Container the operator menu portals into (the drawer's z-layer). */
	menuPortal: HTMLElement | boolean
	/** Set once a blocked save has named this row: ticked, but left empty. */
	error?: string
	/** Why this row cannot be picked with the trigger source currently chosen. */
	unavailable?: string
	/** Whether the workspace's plan covers this row (not which plan owns it). */
	gated: boolean
	/** Opens the plan dialog from the row's upgrade prompt. */
	onUpgrade: () => void
	onToggle: () => void
	onChange: (patch: Partial<RuleCriterion>) => void
}

const CriterionRow = ({
	definition,
	selected,
	menuPortal,
	error,
	unavailable,
	gated,
	onUpgrade,
	onToggle,
	onChange
}: CriterionRowProps) => {
	const expanded = Boolean(selected) && definition.kind !== 'boolean'

	const row = (
		// `w-full` because the DS Tooltip wrapper is `cg-inline-flex`, which would size
		// a gated row to its text.
		<div className="flex w-full items-start justify-between gap-3">
			<Checkbox
				size="sm"
				label={definition.label}
				description={definition.hint}
				checked={Boolean(selected)}
				disabled={gated || Boolean(unavailable)}
				onChange={onToggle}
			/>
			{gated ? (
				<Badge variant="brand" size="sm" className="self-center">
					{definition.tier}
				</Badge>
			) : null}
		</div>
	)

	if (unavailable)
		return (
			<div>
				<div className={TOOLTIP_FIT}>
					<Tooltip
						content={definition.label}
						description={unavailable}
						position="top"
						showArrow
						portal={menuPortal}
					>
						{row}
					</Tooltip>
				</div>
			</div>
		)

	return (
		<div>
			{gated ? (
				// Each tooltip names its own row's tier: on lite, Standard and Pro are both gated.
				<div className={TOOLTIP_FIT}>
					<Tooltip
						content={`${definition.tier} feature`}
						description={
							<>
								Upgrade your plan to {definition.tier} to turn this on.
								{/* A button, not an `a`: with no account in hand a real href would be dead. */}
								<button
									type="button"
									className={UPGRADE_LINK}
									onClick={onUpgrade}
								>
									Upgrade plan
								</button>
							</>
						}
						position="top"
						showArrow
						// Into the drawer's z-[9999] root, not document.body: the DS body ships `cg-z-50`.
						portal={menuPortal}
					>
						{row}
					</Tooltip>
				</div>
			) : (
				row
			)}
			{expanded ? (
				// The anchor sits on the sub-form, not the row: the row's Checkbox input is `sr-only`.
				<div
					data-field={`criterion:${definition.id}`}
					className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-[rgb(var(--cg-surface-2))] p-3"
				>
					{definition.operators ? (
						// Fixed width so picking an operator does not resize the field. justify-between via
						// the child selector — a plain utility cannot beat `cg-justify-center`.
						<Dropdown
							className="w-32 [&>button]:w-full [&>button]:justify-between [&>div>button]:w-full [&>div>button]:justify-between"
							options={definition.operators.map((operator) => ({
								value: operator,
								label: operator
							}))}
							value={selected?.operator || definition.operators[0]}
							size="md"
							onValueChange={(value) =>
								onChange({ operator: value as CriterionOperator })
							}
							portal={menuPortal}
						/>
					) : null}
					{definition.kind === 'country' ? (
						<CountryPicker
							className={`min-w-0 flex-1 ${error ? invalidControl : ''}`}
							values={selected?.values ?? []}
							onChange={(values) => onChange({ values })}
							placeholder={definition.placeholder ?? 'Any country'}
							ariaLabel={`${definition.label} value`}
						/>
					) : definition.kind === 'choice' ? (
						<MultiSelect
							className={`min-w-0 flex-1 ${error ? invalidControl : ''}`}
							options={(definition.options ?? []).map((option) => ({
								value: option,
								label: option
							}))}
							values={selected?.values ?? []}
							onChange={(values) => onChange({ values })}
							placeholder="Any"
							ariaLabel={`${definition.label} value`}
							portalTo={menuPortal}
						/>
					) : definition.kind === 'keywords' ? (
						<KeywordInput
							className={`min-w-0 flex-1 ${surfaceFor(Boolean(error))}`}
							values={selected?.values ?? []}
							onChange={(values) => onChange({ values })}
							placeholder={definition.placeholder}
							ariaLabel={`${definition.label} value`}
							max={definition.maxStoredValues}
						/>
					) : (
						<>
							<div className="w-40">
								<Input
									aria-label={`${definition.label} value`}
									placeholder={
										definition.placeholder ??
										(definition.kind === 'number' ? '-' : undefined)
									}
									size="sm"
									state={error ? 'error' : 'default'}
									className={surfaceFor(Boolean(error))}
									type={definition.kind === 'number' ? 'number' : 'text'}
									autoComplete="off"
									value={selected?.values[0] || ''}
									onChange={(event) =>
										onChange({ values: [event.target.value] })
									}
								/>
							</div>
							{definition.unit ? (
								<span className="text-[13px] text-[rgb(var(--cg-text-secondary))]">
									{definition.unit}
								</span>
							) : null}
						</>
					)}
				</div>
			) : null}
			<FieldError>{error}</FieldError>
		</div>
	)
}

/** A field the rule cannot be saved without, and what to say about it. */
interface FieldProblem {
	/** Matches the `data-field` on the field's wrapper, for scroll-and-focus. */
	field: string
	message: string
}

/** Every obligation the draft has not met, in the order the form asks them — one
 *  pass for both the save gate and the copy, and "the first problem" is this order. */
const findProblems = (draft: RuleDraft): FieldProblem[] => {
	const problems: FieldProblem[] = []
	const add = (field: string, message: string) =>
		problems.push({ field, message })

	if (!draft.name.trim()) add('name', 'Name the rule.')

	if (draft.scope.mode !== 'website' && draft.scope.values.length === 0)
		add(
			'scope',
			draft.scope.mode === 'accounts'
				? 'Pick at least one ad account.'
				: 'Pick at least one campaign.'
		)

	// The `=== ''` test comes first: it is what narrows the field to a number.
	if (draft.trigger.maxClicks === '' || draft.trigger.maxClicks < 0)
		add('trigger.maxClicks', 'Maximum ad clicks must be 0 or more.')
	if (draft.trigger.periodValue === '' || draft.trigger.periodValue < 1)
		add('trigger.periodValue', 'The period must be 1 or more.')
	if (!draft.trigger.focus) add('trigger.focus', 'Pick what the clicks share.')

	// Only owed once a criterion exists to count over, or a percentage-mode draft
	// blocks on a hidden control.
	if (
		draft.criteria.length > 0 &&
		draft.threshold.mode === 'percentage' &&
		(draft.threshold.percent === '' || draft.threshold.percent < 1)
	)
		add('threshold', 'The percentage must be 1 or more.')

	// A ticked-but-empty criterion is dropped by the summary, so the rule would save
	// one step wider than the form shows. Booleans and Conversion are exempt.
	draft.criteria.forEach((criterion) => {
		const definition = CRITERIA_DEFINITIONS.find(
			(entry) => entry.id === criterion.id
		)
		if (!definition) return

		// Checked before the exemption below, because the criterion this catches is an
		// `optionalValue` one: a value on `is not` would save as its own opposite.
		if (
			definition.negatedValueProblem &&
			criterion.operator === 'is not' &&
			criterion.values.some((value) => value.trim() !== '')
		)
			add(`criterion:${criterion.id}`, definition.negatedValueProblem)

		if (definition.kind === 'boolean' || definition.optionalValue) return
		if (!criterion.values.some((value) => value.trim() !== '')) {
			add(`criterion:${criterion.id}`, 'Give this a value, or untick it.')
			return
		}
		// The country row writes ISO codes, and a name with no code is dropped. If the
		// whole list drops, the modifier saves as `values: []` — which makes the engine
		// skip the ENTIRE rule, not just this criterion.
		if (
			definition.kind === 'country' &&
			!criterion.values.some((value) => countryCodeByName.has(value))
		)
			add(
				`criterion:${criterion.id}`,
				'Pick a country from the list, or untick this.'
			)
	})

	if (!draft.actions.clickStatus)
		add('actions.clickStatus', 'Pick the status to set.')
	if (draft.actions.addLabels && !draft.actions.labels.trim())
		add('actions.labels', 'Type a label, or untick the box.')
	if (draft.actions.blacklist && !(draft.actions.blacklistValue >= 1))
		add('actions.blacklist', 'Enter how long to blacklist for.')

	return problems
}

// Gated criteria get their own card, split by what THIS workspace can use.
const partitionCriteria = (accountTier: string | null, isTrial: boolean) => {
	const gated = CRITERIA_DEFINITIONS.filter((definition) =>
		isCriterionGated(definition, accountTier, isTrial)
	)
	return {
		gated,
		standard: CRITERIA_DEFINITIONS.filter(
			(definition) => !gated.includes(definition)
		)
	}
}

export const RuleSheet = ({
	open,
	rule,
	initialDraft,
	templateName,
	template,
	onClose,
	onSave,
	onSaveTemplate
}: RuleSheetProps) => {
	const [draft, setDraft] = useState<RuleDraft>(() =>
		draftFromRule(rule, initialDraft)
	)
	const entities = useRuleEntities()
	// Trials carry every feature, the same exemption `accountStore.hasFeature` makes.
	const accountTier = useAccountStore((s) => s.tier)()
	const isTrial = useAccountStore(
		(s) => s.account?.billing?.subscription?.type === 'TRIAL'
	)
	const { standard: standardCriteria, gated: gatedCriteria } = useMemo(
		() => partitionCriteria(accountTier, isTrial),
		[accountTier, isTrial]
	)
	// Mounted INSIDE the drawer's z-[9999] root — the DS Modal renders in place.
	const [planDialogOpen, setPlanDialogOpen] = useState(false)
	const scopeOptions = useMemo(
		() => scopeChoices(entities.website),
		[entities.website]
	)
	// Stays mounted on the way out so the exit animates; the DS hook waits two frames.
	const { mounted: render, entered: shown } = useEnterExit(open, {
		duration: SHEET_MOTION_MS
	})
	// Dropdown menus must portal INTO the drawer's z-[9999] root — a document.body
	// portal paints at the DS's z-50, UNDER the drawer. State, not a ref: the
	// Dropdowns need a re-render once the root exists.
	const [menuRoot, setMenuRoot] = useState<HTMLDivElement | null>(null)

	const [summaryCap, setSummaryCap] = useState(SUMMARY_CAP_DEFAULT)
	const [resizing, setResizing] = useState(false)
	// Nothing is marked until a save has been refused; after that the marks stay live.
	const [showErrors, setShowErrors] = useState(false)
	const bodyRef = useRef<HTMLDivElement>(null)
	const summaryRef = useRef<HTMLDivElement>(null)

	/** The lowest of SUMMARY_CAP_MAX, the form's own floor and the summary's height —
	 *  the last so dragging up on a short rule cannot raise a cap nothing reaches. A DOM
	 *  read, since the first two depend on the drawer height. */
	const capCeiling = () => {
		const bodyHeight = bodyRef.current?.getBoundingClientRect().height ?? 0
		const content = summaryRef.current?.scrollHeight ?? SUMMARY_CAP_MAX
		return Math.max(
			SUMMARY_CAP_MIN,
			Math.min(
				SUMMARY_CAP_MAX,
				content,
				summaryCap + (bodyHeight - SUMMARY_FORM_FLOOR)
			)
		)
	}

	const clampCap = (next: number, ceiling: number) =>
		Math.round(Math.min(ceiling, Math.max(SUMMARY_CAP_MIN, next)))

	const startResize = (event: ReactPointerEvent<HTMLDivElement>) => {
		// Otherwise the drag starts a text selection across the whole drawer.
		event.preventDefault()
		const handle = event.currentTarget
		const startY = event.clientY
		const startCap = summaryCap
		const ceiling = capCeiling()
		// Capture, so the drag keeps following the pointer once it leaves the 10px strip.
		handle.setPointerCapture(event.pointerId)
		setResizing(true)
		// Up is bigger: the band grows towards the pointer, not away from it.
		const move = (moved: PointerEvent) =>
			setSummaryCap(clampCap(startCap + (startY - moved.clientY), ceiling))
		const stop = () => {
			handle.releasePointerCapture(event.pointerId)
			handle.removeEventListener('pointermove', move)
			handle.removeEventListener('pointerup', stop)
			handle.removeEventListener('pointercancel', stop)
			setResizing(false)
		}
		handle.addEventListener('pointermove', move)
		handle.addEventListener('pointerup', stop)
		handle.addEventListener('pointercancel', stop)
	}

	const resizeByKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
		const step = event.shiftKey ? SUMMARY_CAP_STEP_COARSE : SUMMARY_CAP_STEP
		const ceiling = capCeiling()
		if (event.key === 'ArrowUp')
			setSummaryCap((cap) => clampCap(cap + step, ceiling))
		else if (event.key === 'ArrowDown')
			setSummaryCap((cap) => clampCap(cap - step, ceiling))
		else if (event.key === 'Home') setSummaryCap(SUMMARY_CAP_MIN)
		else if (event.key === 'End') setSummaryCap(ceiling)
		else return
		// Only after we know it was ours: the drawer scrolls on arrows otherwise.
		event.preventDefault()
	}

	// The pointer spends the drag off the handle, so the cursor and selection have to
	// be held on the document.
	useEffect(() => {
		if (!resizing) return
		const { style } = document.body
		const previousCursor = style.cursor
		const previousSelect = style.userSelect
		style.cursor = 'ns-resize'
		style.userSelect = 'none'
		return () => {
			style.cursor = previousCursor
			style.userSelect = previousSelect
		}
	}, [resizing])

	useEffect(() => {
		if (!open) return
		setDraft(draftFromRule(rule, initialDraft))
		// A fresh draft has never been refused, so it opens unmarked.
		setShowErrors(false)
		// Reset on the RULE, not every render, so a drag is not inherited by the next rule.
		setSummaryCap(SUMMARY_CAP_DEFAULT)
	}, [open, rule, initialDraft])

	// Esc closes, unless a dropdown inside the drawer owns it; lock the page scroll.
	useEffect(() => {
		if (!open) return
		const onEsc = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return
			if (menuRoot?.querySelector(':scope > :not(form):not([aria-hidden])'))
				return
			onClose()
		}
		document.addEventListener('keydown', onEsc)
		const previousOverflow = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.removeEventListener('keydown', onEsc)
			document.body.style.overflow = previousOverflow
		}
	}, [open, onClose, menuRoot])

	// Third-party chat widgets pin themselves to the max z-index, so drop them behind
	// the drawer while it is open. The write MUST be `!important` — custom-rules-page.css
	// lifts the launcher with `z-index: 10000 !important`.
	useEffect(() => {
		if (!open) return
		const selectors = [
			'#hubspot-messages-iframe-container',
			'#hubspot-conversations-iframe',
			'#ug-product-updates-frame',
			'.widget-align-right'
		]
		// Dedupe: the HubSpot launcher answers to BOTH its id and `.widget-align-right`,
		// and the second save would restore the 0 the first one just wrote.
		const targets = new Set<HTMLElement>()
		selectors.forEach((selector) =>
			document
				.querySelectorAll<HTMLElement>(selector)
				.forEach((el) => targets.add(el))
		)
		const saved = [...targets].map((el) => {
			const entry = {
				el,
				z: el.style.getPropertyValue('z-index'),
				priority: el.style.getPropertyPriority('z-index')
			}
			el.style.setProperty('z-index', '0', 'important')
			return entry
		})
		return () =>
			saved.forEach(({ el, z, priority }) => {
				el.style.removeProperty('z-index')
				if (z) el.style.setProperty('z-index', z, priority)
			})
	}, [open])

	const updateDraft = <Key extends keyof RuleDraft>(
		key: Key,
		value: RuleDraft[Key]
	) => {
		setDraft((current) => ({ ...current, [key]: value }))
	}

	const toggleCriterion = (id: string) => {
		setDraft((current) => {
			if (current.criteria.some((criterion) => criterion.id === id)) {
				return {
					...current,
					criteria: current.criteria.filter((criterion) => criterion.id !== id)
				}
			}
			const definition = CRITERIA_DEFINITIONS.find((entry) => entry.id === id)
			return {
				...current,
				criteria: [
					...current.criteria,
					{ id, operator: definition?.operators?.[0], values: [] }
				]
			}
		})
	}

	const updateCriterion = (id: string, patch: Partial<RuleCriterion>) => {
		setDraft((current) => ({
			...current,
			criteria: current.criteria.map((criterion) =>
				criterion.id === id ? { ...criterion, ...patch } : criterion
			)
		}))
	}

	const updateActions = (patch: Partial<RuleDraft['actions']>) => {
		setDraft((current) => {
			const actions = { ...current.actions, ...patch }
			// Blacklisting always excludes downstream too, so both exclusions follow the box
			// in BOTH directions. Either can still be ticked on its own.
			if ('blacklist' in patch) {
				actions.tempIpExclusion = actions.blacklist
				actions.audienceExclusion = actions.blacklist
			}
			return { ...current, actions }
		})
	}

	// Derived from what the rule TARGETS, through the same helper `toCustomRule` uses.
	const platform = useMemo(
		() =>
			resolveRulePlatform(draft.scope, entities.adAccounts, entities.campaigns),
		[draft.scope, entities.adAccounts, entities.campaigns]
	)

	const problems = findProblems(draft)
	// Guards the double-submit an awaited save now makes reachable.
	const [saving, setSaving] = useState(false)
	/** A field's message, once a refused save has made the problems visible. */
	const problemFor = (field: string) =>
		showErrors
			? problems.find((problem) => problem.field === field)?.message
			: undefined

	/** Take the author to the first unanswered field — on a form this tall it is usually out of sight. */
	const focusProblem = (field: string) => {
		const anchor = bodyRef.current?.querySelector<HTMLElement>(
			`[data-field="${field}"]`
		)
		if (!anchor) return
		anchor.scrollIntoView({ block: 'center', behavior: 'smooth' })
		// `preventScroll` because the smooth scroll above is already on its way.
		anchor
			.querySelector<HTMLElement>('input, textarea, button')
			?.focus({ preventScroll: true })
	}

	// Closes only once the write has landed. It used to close on the call, so a rejected
	// save took the drawer and the user's draft with it and said nothing; the page toasts
	// and rethrows, and everything typed is still here to retry.
	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault()
		if (problems.length) {
			setShowErrors(true)
			// After the commit, so the jump lands on the field WITH its message.
			requestAnimationFrame(() => focusProblem(problems[0].field))
			return
		}
		if (saving) return

		setSaving(true)
		try {
			// Template mode hands the draft back to the library — no rule is minted.
			if (template) {
				await onSaveTemplate?.({ ...draft, name: draft.name.trim() })
			} else {
				await onSave({
					...draft,
					name: draft.name.trim(),
					id: rule?.id || `rule-${Date.now()}`,
					platform,
					level: rule?.level || 'Website',
					status: rule?.status || 'active',
					triggered: rule?.triggered || 0,
					blocked: rule?.blocked || 0,
					updatedAt: 'Just now',
					lastExecutedAt: 'Never'
				})
			}
			onClose()
		} catch {
			// The page owns the message; this only has to keep the drawer open.
		} finally {
			setSaving(false)
		}
	}

	/** Criteria the new trigger source cannot observe are dropped with the change,
	 *  rather than left ticked behind a disabled control. */
	const selectTriggerSource = (value: string) =>
		setDraft((current) => ({
			...current,
			trigger: { ...current.trigger, focus: value },
			criteria: current.criteria.filter(
				(criterion) =>
					criterionDefinition(criterion.id)?.unavailableWhenSource !== value
			)
		}))

	const hasCriteria = draft.criteria.length > 0

	const renderCriterion = (definition: CriterionDefinition) => (
		<CriterionRow
			key={definition.id}
			definition={definition}
			selected={draft.criteria.find(
				(criterion) => criterion.id === definition.id
			)}
			menuPortal={menuRoot ?? true}
			error={problemFor(`criterion:${definition.id}`)}
			gated={isCriterionGated(definition, accountTier, isTrial)}
			onUpgrade={() => setPlanDialogOpen(true)}
			unavailable={
				definition.unavailableWhenSource === draft.trigger.focus
					? definition.unavailableReason
					: undefined
			}
			onToggle={() => toggleCriterion(definition.id)}
			onChange={(patch) => updateCriterion(definition.id, patch)}
		/>
	)

	if (!render) return null

	return createPortal(
		<div
			ref={setMenuRoot}
			className="fixed inset-0 z-[9999]"
			role="dialog"
			aria-modal="true"
			aria-label={
				template
					? 'Edit template'
					: rule
					? 'Edit custom rule'
					: 'New custom rule'
			}
		>
			{/* aria-hidden also lets the Esc guard tell it apart from a portaled menu. */}
			<div
				aria-hidden="true"
				className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
					shown ? 'opacity-100' : 'opacity-0'
				}`}
				onClick={onClose}
			/>
			{/* overflow-hidden clips the scrolling body to the radius. */}
			<form
				onSubmit={handleSubmit}
				// Chrome's field-history popup cannot be dismissed by a page click.
				autoComplete="off"
				// The form does its own checking; `min` on the number fields would otherwise
				// have Chrome refuse the submit before `handleSubmit` ran.
				noValidate
				className={`absolute bottom-2 right-2 top-2 flex w-[460px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-xl border border-[rgb(var(--cg-border))] bg-white shadow-[0px_8px_24px_-4px_rgba(16,24,40,0.18)] transition-transform duration-300 ease-out ${
					shown ? 'translate-x-0' : 'translate-x-[calc(100%+8px)]'
				}`}
			>
				{/* Header - bottom border gives the scrolling body a clean cut-off. */}
				<div className="flex items-start justify-between gap-4 border-b border-[rgb(var(--cg-border))] px-6 pb-4 pt-6">
					<div className="flex min-w-0 flex-col gap-1">
						{/* One step above the section titles below it, which are 16px. */}
						<h2 className="text-xl font-medium text-[rgb(var(--cg-brand-dark))]">
							{template
								? 'Edit template'
								: rule
								? 'Edit custom rule'
								: 'New custom rule'}
						</h2>
						{!rule && templateName ? (
							<span className="flex items-center gap-1.5 text-[13px] text-[rgb(var(--cg-text-secondary))]">
								<LayersTwo01 className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--cg-text-tertiary))]" />
								<span className="truncate">
									Started from{' '}
									<span className="font-medium text-[rgb(var(--cg-text-primary))]">
										{templateName}
									</span>
								</span>
							</span>
						) : null}
					</div>
					{/* DS AppIconButton: the hand-rolled one carried no focus ring. The glyph needs
					    its own size (the slot is a fixed 16px box), and the `!` because twMerge
					    cannot dedupe `cg-` classes. */}
					<AppIconButton
						variant="ghost"
						size="mini"
						aria-label="Close"
						onClick={onClose}
						icon={<XClose className="h-3 w-3" />}
						className="!text-[rgb(var(--cg-text-secondary))] hover:!text-[rgb(var(--cg-text-primary))]"
					/>
				</div>

				{/* `relative` is load-bearing: the DS Checkbox and Radio hide their real input
				    with `sr-only` (position:absolute), which without a positioned ancestor
				    resolves to the panel <form> and stretches its scroll height — clicking a
				    checkbox then scrolls the header off the top. */}
				<FadingScrollArea
					ref={bodyRef}
					className="relative min-h-0 flex-1 divide-y divide-[rgb(var(--cg-border))] overflow-y-auto px-6 pb-4 pt-4"
				>
					<Section>
						<SectionTitle
							title="Rule"
							hint="Name the rule and choose where it applies."
						/>
						<div className="flex flex-col gap-4">
							<div data-field="name">
								<Input
									label="Rule name"
									placeholder="e.g. Block repeat clicks from data centers"
									size="sm"
									state="default"
									className={surfaceFor(Boolean(problemFor('name')))}
									error={problemFor('name')}
									value={draft.name}
									onChange={(event) => updateDraft('name', event.target.value)}
									autoComplete="off"
									autoFocus
								/>
							</div>
							{/* Vertical resize only: `resize` defaults to `both`, and widening a field
							    inside a fixed-width sheet pushes it over its neighbours. A textarea
							    renders `\n` in a placeholder, an input does not. */}
							<Textarea
								label="Description"
								placeholder={
									'Optional\ne.g. Repeat clicks from a data center are never a real customer, so block the source for a day.'
								}
								size="sm"
								state="default"
								className={`${fieldSurface} resize-y`}
								rows={3}
								value={draft.description}
								onChange={(event) =>
									updateDraft('description', event.target.value)
								}
								autoComplete="off"
							/>
							<div>
								<FieldLabel>For traffic from</FieldLabel>
								<Dropdown
									className={fullWidthTrigger}
									options={scopeOptions}
									value={draft.scope.mode}
									size="md"
									onValueChange={(value) =>
										// An account id is not a campaign id.
										updateDraft('scope', {
											mode: value as RuleScopeMode,
											values: []
										})
									}
									menuWidth="trigger"
									portal={menuRoot ?? true}
								/>
								{draft.scope.mode !== 'website' && (
									<div className="mt-2" data-field="scope">
										<MultiSelect
											className={
												problemFor('scope') ? invalidControl : undefined
											}
											options={
												draft.scope.mode === 'accounts'
													? entities.adAccounts.map((account) => ({
															value: account.id,
															label: account.name,
															platform: account.platform
													  }))
													: entities.campaigns.map((campaign) => ({
															value: campaign.id,
															label: campaign.name,
															platform: entities.adAccounts.find(
																(account) => account.id === campaign.accountId
															)?.platform
													  }))
											}
											values={draft.scope.values}
											onChange={(values) =>
												updateDraft('scope', {
													...draft.scope,
													values
												})
											}
											maxInline={1}
											placeholder={
												draft.scope.mode === 'accounts'
													? 'Select ad accounts'
													: 'Select campaigns'
											}
											ariaLabel={
												draft.scope.mode === 'accounts'
													? 'Ad accounts'
													: 'Campaigns'
											}
											searchable
											searchPlaceholder={
												draft.scope.mode === 'accounts'
													? 'Search ad accounts'
													: 'Search campaigns'
											}
											portalTo={menuRoot ?? true}
										/>
										<FieldError>{problemFor('scope')}</FieldError>
									</div>
								)}
							</div>
						</div>
					</Section>

					<Section>
						<SectionTitle
							title="Trigger"
							hint="The click frequency that makes this rule fire."
						/>
						<div className="flex flex-col gap-3">
							<div className="flex items-end gap-3">
								<div
									className="w-[132px] shrink-0"
									data-field="trigger.maxClicks"
								>
									<FieldLabel>Maximum ad clicks</FieldLabel>
									<Input
										aria-label="Maximum ad clicks"
										size="sm"
										state={
											problemFor('trigger.maxClicks') ? 'error' : 'default'
										}
										className={surfaceFor(
											Boolean(problemFor('trigger.maxClicks'))
										)}
										type="number"
										placeholder="-"
										min={0}
										value={String(draft.trigger.maxClicks)}
										onChange={(event) =>
											updateDraft('trigger', {
												...draft.trigger,
												// Clearing lands as '', not the 0 `Number` gives.
												maxClicks:
													event.target.value === ''
														? ''
														: Number(event.target.value)
											})
										}
									/>
								</div>
								<div className="flex min-w-0 flex-1 flex-col">
									<FieldLabel>Within</FieldLabel>
									<div className="flex items-center gap-3">
										<div
											className="w-16 shrink-0"
											data-field="trigger.periodValue"
										>
											<Input
												aria-label="Period value"
												size="sm"
												state={
													problemFor('trigger.periodValue')
														? 'error'
														: 'default'
												}
												className={surfaceFor(
													Boolean(problemFor('trigger.periodValue'))
												)}
												type="number"
												placeholder="-"
												min={1}
												value={String(draft.trigger.periodValue)}
												onChange={(event) =>
													updateDraft('trigger', {
														...draft.trigger,
														periodValue:
															event.target.value === ''
																? ''
																: Number(event.target.value)
													})
												}
											/>
										</div>
										<Dropdown
											className={`min-w-0 flex-1 ${fullWidthTrigger}`}
											options={PERIOD_OPTIONS}
											value={draft.trigger.periodUnit}
											size="md"
											onValueChange={(value) =>
												updateDraft('trigger', {
													...draft.trigger,
													periodUnit:
														value as RuleDraft['trigger']['periodUnit']
												})
											}
											menuWidth="trigger"
											portal={menuRoot ?? true}
										/>
									</div>
								</div>
							</div>
							{/* Both messages under the row: a sentence in the 64px box wraps to four lines. */}
							<FieldError>{problemFor('trigger.maxClicks')}</FieldError>
							<FieldError>{problemFor('trigger.periodValue')}</FieldError>
							<div data-field="trigger.focus">
								<FieldLabel>From the same</FieldLabel>
								<Dropdown
									className={`${fullWidthTrigger} ${
										problemFor('trigger.focus') ? invalidControl : ''
									}`}
									options={FOCUS_OPTIONS}
									value={draft.trigger.focus}
									placeholder="Select a source"
									size="md"
									onValueChange={(value) => selectTriggerSource(value)}
									menuWidth="trigger"
									portal={menuRoot ?? true}
								/>
								<FieldError>{problemFor('trigger.focus')}</FieldError>
							</div>
						</div>
					</Section>

					<Section>
						<SectionTitle
							title="Additional criteria"
							hint="Narrow the rule to clicks matching extra signals."
						/>
						{/* Leads the section because the threshold governs every criterion below,
					    and stays mounted-but-disabled so the list never shifts on the first pick. */}
						<div className="mb-6 rounded-lg bg-[rgb(var(--cg-surface-2))] p-3">
							{/* One sentence read top to bottom; the caption names WHICH thing
						    the clicks share. */}
							<FieldLabel muted={!hasCriteria}>
								Rule will be triggered if
							</FieldLabel>
							<div className="flex items-center gap-2">
								<Dropdown
									className={`w-[212px] shrink-0 ${stretchTrigger}`}
									options={THRESHOLD_MODE_OPTIONS}
									value={draft.threshold.mode}
									size="md"
									disabled={!hasCriteria}
									ariaLabel="Clicks the criteria apply to"
									onValueChange={(value) =>
										updateDraft('threshold', {
											...draft.threshold,
											mode: value as RuleDraft['threshold']['mode']
										})
									}
									menuWidth="trigger"
									portal={menuRoot ?? true}
								/>
								{hasCriteria && draft.threshold.mode === 'percentage' ? (
									<div className="flex items-center gap-1.5">
										<div className="w-[76px]" data-field="threshold">
											<Input
												aria-label="Criteria threshold percentage"
												size="sm"
												state={problemFor('threshold') ? 'error' : 'default'}
												className={surfaceFor(Boolean(problemFor('threshold')))}
												type="number"
												placeholder="-"
												// min is 5, not 1: the DS chevrons call the
												// native stepUp(), which counts from min.
												min={5}
												max={100}
												step={5}
												value={String(draft.threshold.percent)}
												onChange={(event) =>
													updateDraft('threshold', {
														...draft.threshold,
														percent:
															event.target.value === ''
																? ''
																: Number(event.target.value)
													})
												}
											/>
										</div>
										{/* Outside the field: the DS Input drops its steppers once it carries
									    a trailing icon or addon. */}
										<span className="text-base text-[rgb(var(--cg-text-secondary))]">
											%
										</span>
									</div>
								) : null}
							</div>
							<p
								className={`mt-2 text-[13px] leading-[18px] ${
									hasCriteria
										? 'text-[rgb(var(--cg-text-secondary))]'
										: 'text-[rgb(var(--cg-text-disabled))]'
								}`}
							>
								{/* Drops out while the source is unpicked: a stand-in would state a
							    choice the form has not been given. */}
								{draft.trigger.focus && (
									<>
										from the same{' '}
										<span
											className={
												hasCriteria
													? 'font-semibold text-[rgb(var(--cg-text-primary))]'
													: 'font-semibold'
											}
										>
											{draft.trigger.focus}
										</span>{' '}
									</>
								)}
								match the criteria
							</p>
							<FieldError>{problemFor('threshold')}</FieldError>
						</div>
						<div className="flex flex-col gap-3">
							{standardCriteria.map(renderCriterion)}
						</div>
						{gatedCriteria.length > 0 && (
							<div className="mt-4 rounded-lg bg-[rgb(var(--cg-surface-2))] p-3">
								<div className="flex flex-col gap-3">
									{gatedCriteria.map(renderCriterion)}
								</div>
							</div>
						)}
					</Section>

					<Section>
						<SectionTitle
							title="Actions"
							hint="What ClickGuard does when the rule fires."
						/>
						<div className="flex flex-col gap-3">
							{/* A labeled field, not a checked-and-disabled checkbox, which would read
						    as "unavailable" rather than "always on". */}
							<div data-field="actions.clickStatus">
								<FieldLabel>Set click status</FieldLabel>
								<Dropdown
									className={`w-40 [&>button]:w-full [&>button]:justify-between [&>div>button]:w-full [&>div>button]:justify-between ${
										problemFor('actions.clickStatus') ? invalidControl : ''
									}`}
									options={CLICK_STATUS_OPTIONS}
									value={draft.actions.clickStatus}
									placeholder="Select a status"
									size="md"
									onValueChange={(value) =>
										updateActions({
											clickStatus: value as ClickStatus
										})
									}
									menuWidth="trigger"
									portal={menuRoot ?? true}
								/>
								<FieldError>{problemFor('actions.clickStatus')}</FieldError>
							</div>
							<div>
								<Checkbox
									size="sm"
									label="Add labels to click"
									checked={draft.actions.addLabels}
									onChange={(event) =>
										updateActions({ addLabels: event.target.checked })
									}
								/>
								{draft.actions.addLabels ? (
									<div className="mt-2" data-field="actions.labels">
										<Input
											aria-label="Click labels"
											placeholder="Comma-separated labels"
											size="sm"
											state="default"
											className={surfaceFor(
												Boolean(problemFor('actions.labels'))
											)}
											error={problemFor('actions.labels')}
											autoComplete="off"
											value={draft.actions.labels}
											onChange={(event) =>
												updateActions({ labels: event.target.value })
											}
										/>
									</div>
								) : null}
							</div>
							<div>
								<Checkbox
									size="sm"
									label="Blacklist the source"
									checked={draft.actions.blacklist}
									onChange={(event) =>
										updateActions({ blacklist: event.target.checked })
									}
								/>
								{draft.actions.blacklist ? (
									<div className="mt-2" data-field="actions.blacklist">
										<div className="flex items-center gap-2">
											<span className="text-[13px] text-[rgb(var(--cg-text-secondary))]">
												for
											</span>
											<div className="w-16 shrink-0">
												<Input
													aria-label="Blacklist duration"
													size="sm"
													state={
														problemFor('actions.blacklist')
															? 'error'
															: 'default'
													}
													className={surfaceFor(
														Boolean(problemFor('actions.blacklist'))
													)}
													type="number"
													min={1}
													value={String(draft.actions.blacklistValue)}
													onChange={(event) =>
														updateActions({
															blacklistValue: Number(event.target.value)
														})
													}
												/>
											</div>
											<Dropdown
												options={PERIOD_OPTIONS}
												value={draft.actions.blacklistUnit}
												size="md"
												onValueChange={(value) =>
													updateActions({
														blacklistUnit:
															value as RuleDraft['actions']['blacklistUnit']
													})
												}
												portal={menuRoot ?? true}
											/>
										</div>
										<FieldError>{problemFor('actions.blacklist')}</FieldError>
									</div>
								) : null}
							</div>
							<Checkbox
								size="sm"
								label="Temporarily exclude IP address on targeted campaign"
								description={
									draft.actions.blacklist
										? 'Included when blacklisting the source'
										: 'Google Ads only'
								}
								// Blacklisting stores both exclusions as absent, so a saved
								// blacklisting rule reads back with these false — mirror the
								// old page and derive the tick from the blacklist itself.
								checked={
									draft.actions.blacklist || draft.actions.tempIpExclusion
								}
								disabled={draft.actions.blacklist}
								onChange={(event) =>
									updateActions({ tempIpExclusion: event.target.checked })
								}
							/>
							<Checkbox
								size="sm"
								label="Add visitor to excluded audience on targeted PPC account"
								description={
									draft.actions.blacklist
										? 'Included when blacklisting the source'
										: undefined
								}
								checked={
									draft.actions.blacklist || draft.actions.audienceExclusion
								}
								disabled={draft.actions.blacklist}
								onChange={(event) =>
									updateActions({
										audienceExclusion: event.target.checked
									})
								}
							/>
						</div>
					</Section>
				</FadingScrollArea>

				{/* Summary — the SAME component the row's details drawer renders, capped and
				    scrolled internally. The cap is dragged (see SUMMARY_CAP_* above); the
				    heading and the 8px/24px padding sit INSIDE the scroller and are counted
				    into every SUMMARY_CAP_*. */}
				<div className="relative shrink-0 border-t border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-canvas))]">
					{/* The band's top border, made draggable — the 11px strip is the target.
					    -6px, not -4: `top` resolves against the PADDING box, which starts after
					    this band's 1px border-t. aria-valuemax is the declared 420, not the live
					    ceiling, which drops as the rule gets shorter. */}
					<div
						role="separator"
						aria-orientation="horizontal"
						aria-label="Resize summary"
						aria-valuenow={summaryCap}
						aria-valuemin={SUMMARY_CAP_MIN}
						aria-valuemax={SUMMARY_CAP_MAX}
						tabIndex={0}
						onPointerDown={startResize}
						onKeyDown={resizeByKey}
						onDoubleClick={() => setSummaryCap(SUMMARY_CAP_DEFAULT)}
						className="group absolute inset-x-0 -top-1.5 z-10 flex h-[11px] cursor-ns-resize items-center justify-center focus:outline-none"
					>
						<span
							className={`h-[3px] w-10 rounded-full transition-colors duration-150 ${
								resizing
									? 'bg-[rgb(var(--cg-brand))]'
									: 'bg-transparent group-hover:bg-[rgb(var(--cg-text-tertiary))] group-focus-visible:bg-[rgb(var(--cg-brand))]'
							}`}
						/>
					</div>
					<FadingScrollArea
						ref={summaryRef}
						style={{ maxHeight: summaryCap }}
						className="overflow-y-auto px-6 py-2"
					>
						<h3 className="mb-1 text-[13px] font-semibold leading-[18px] text-[rgb(var(--cg-text-primary))]">
							Summary
						</h3>
						<RuleLedger dense rule={{ ...draft, platform }} />
					</FadingScrollArea>
				</div>

				<div className="border-t border-[rgb(var(--cg-border))] px-6 py-4">
					<div className="flex items-center justify-between gap-3">
						{showErrors && problems.length ? (
							<p
								role="status"
								className="min-w-0 text-base leading-[18px] text-[rgb(var(--cg-danger))]"
							>
								{problems.length === 1
									? '1 field still needs an answer'
									: `${problems.length} fields still need an answer`}
							</p>
						) : (
							<span />
						)}
						<div className="flex shrink-0 items-center gap-3">
							<AppButton
								type="button"
								label="Cancel"
								variant="secondary"
								roundness="default"
								onClick={onClose}
							/>
							{/* Live even when the rule cannot be saved: it marks every unanswered field. */}
							<AppButton
								type="submit"
								label={
									template
										? 'Save template'
										: rule
										? 'Save rule'
										: 'Create rule'
								}
								variant="primary"
								roundness="default"
								isLoading={saving}
							/>
						</div>
					</div>
				</div>
			</form>

			{/* Inside the drawer's root: the DS Modal renders in place, so page level
			    would put it at the DS's own z-50, under the drawer. */}
			<ChangePlanDialog
				open={planDialogOpen}
				onClose={() => setPlanDialogOpen(false)}
				path={PostHogPath.CUSTOM_RULES}
			/>
		</div>,
		document.body
	)
}
